import { useEffect, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Avatar, Stack, TableRow, TableCell, Typography, MenuItem, Switch } from '@mui/material';
import { TableMoreMenu } from 'src/components/table';
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import { APIS } from 'src/utils/serviceUrls';
import axiosInstance from 'src/utils/axios';
import { useSnackbar } from 'notistack';

// components

// ----------------------------------------------------------------------

type Props = {
  row: any;
  selected: boolean;
  onEditRow: VoidFunction;
  onDeleteRow: VoidFunction;
  onViewRow?: VoidFunction;
  visibleColumns?: string[];
  // Diploma batches this instructor leads ("Diploma · Batch").
  diplomaBatches?: string[];
  // Real number of courses (counted by the course→instructor reference).
  courseCount?: number;
};

export default function InstructorTableRow({
  row,
  selected,
  onEditRow,
  onDeleteRow,
  onViewRow,
  visibleColumns,
  diplomaBatches,
  courseCount,
}: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const showCol = (id: string) => !visibleColumns || visibleColumns.includes(id);

  const { name, email, phone, active, imageUrl, } = row;

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  // Controlled so a refused save can put the switch back where the database is.
  const [isActive, setIsActive] = useState<boolean>(!!active);

  useEffect(() => {
    setIsActive(!!active);
  }, [active]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const activeDeactive = async (active: boolean) => {
    setIsActive(active);
    try {
      // Send ONLY the changed field — re-sending the whole row (with _id,
      // timestamps, image, etc.) trips the update DTO's field validators.
      await axiosInstance.patch(APIS.INSTRUCTORS.PATCH(row?._id || ''), { active });
      enqueueSnackbar('Update success!');
    } catch (error) {
      setIsActive(!active);
      enqueueSnackbar(error?.message || 'Unable to edit instructor!', { variant: 'error' });

      console.error(error);
    }
  };

  return (
    <TableRow hover selected={selected}>
      {showCol('name') && (
        <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar alt={name} src={imageUrl ?? 'IN'} sx={{ mr: 2 }} />
          <Typography
            variant="subtitle2"
            noWrap
            onClick={onViewRow}
            sx={onViewRow ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
          >
            {name}
          </Typography>
        </TableCell>
      )}

      {showCol('email') && <TableCell align="left"> {email}</TableCell>}
      {showCol('phone') && <TableCell align="left"> {phone}</TableCell>}

      {showCol('createdAt') && (
        <TableCell align="left">{courseCount ?? row?.courses?.length ?? 0}</TableCell>
      )}

      {showCol('diplomaBatches') && (
        <TableCell align="left">
          {diplomaBatches && diplomaBatches.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {diplomaBatches.map((b, i) => (
                <Label key={i} color="info">
                  {b}
                </Label>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </TableCell>
      )}

      {showCol('active') && (
        <TableCell align="left">
          <Switch checked={isActive} onChange={(e) => activeDeactive(e.target.checked)} />
        </TableCell>
      )}

      <TableCell align="right">
        <TableMoreMenu
          open={openMenu}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          actions={
            <>
              {onViewRow && (
                <MenuItem
                  onClick={() => {
                    onViewRow();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon={'eva:eye-fill'} />
                  View
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  onEditRow();
                  handleCloseMenu();
                }}
              >
                <Iconify icon={'eva:edit-fill'} />
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onDeleteRow();
                  handleCloseMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                <Iconify icon={'eva:trash-2-outline'} />
                Delete
              </MenuItem>
            </>
          }
        />
      </TableCell>
    </TableRow>
  );
}
